<script setup lang="ts">
import { OdsButton, OdsGrid, OdsGridItem } from "@ods/lidl-web-vuejs";
import { useWindowSize } from "@vueuse/core";

import { getGridboxType } from "@reco/service/src/utils/helpers";
import RecoHeading from "@reco/shared/src/components/RecoHeading.vue";
import type { LocaleContext } from "@reco/shared/src/composables/useLocale";
import type { GridBox } from "@reco/shared/src/types/gridbox";

import { GRID_BATCH_SIZE_L, GRID_BATCH_SIZE_S, SCREEN_WIDTH_BREAKPOINT } from "@/constants";
import { prepareGridData } from "@/utils/tracking";

type RenderItem = { isPlaceholder: true; id: number } | { isPlaceholder: false; product: GridBox };

type Props = {
  products: GridBox[];
  heading: string;
  subheading?: string;
  showMoreText: string;
  tracking: string;
  localeContext: LocaleContext;
  isLoading?: boolean;
};
const props = defineProps<Props>();

const { width } = useWindowSize();

const numberOfRows = ref(1);
const maxNumberOfProductsPerRow = computed(() =>
  width.value < SCREEN_WIDTH_BREAKPOINT ? GRID_BATCH_SIZE_S : GRID_BATCH_SIZE_L,
);
const rowSize = computed(() => Math.min(maxNumberOfProductsPerRow.value, props.products.length));

const productsToShowCount = computed(() => Math.min(numberOfRows.value * rowSize.value, props.products.length));
const visibleProducts = computed(() => props.products.slice(0, productsToShowCount.value));

const itemsToRender = computed<RenderItem[]>(() => {
  if (props.isLoading) {
    return Array.from({ length: maxNumberOfProductsPerRow.value }, (_, i) => ({ isPlaceholder: true as const, id: i }));
  }
  return visibleProducts.value.map((product) => ({ isPlaceholder: false as const, product }));
});

const showMore = (): void => {
  numberOfRows.value++;
};
</script>

<template>
  <section class="products-grid">
    <header>
      <RecoHeading :sub-heading="subheading">
        {{ heading }}
      </RecoHeading>
    </header>
    <OdsGrid>
      <OdsGridItem
        v-for="item in itemsToRender"
        :key="item.isPlaceholder ? `skeleton-${item.id}` : `reco-product-${item.product.productId}`"
        :width="12"
        :width-tablet-portrait="6"
        :width-tablet-landscape="4"
        :width-desktop="4"
      >
        <GridboxSkeleton v-if="item.isPlaceholder" />
        <div
          v-else
          data-grid-label="grid-fragment-v2"
          :data-grid-data="prepareGridData(item.product)"
          :data-country="localeContext.country"
          :data-language="localeContext.language"
          :data-type="getGridboxType(props.localeContext.country, item.product.list)"
        />
      </OdsGridItem>
    </OdsGrid>
    <OdsButton
      v-if="productsToShowCount < products.length"
      :label="showMoreText"
      level="primary"
      data-ga-category="Recommendations"
      data-ga-action="showmore"
      :data-ga-label="tracking"
      @click="showMore"
    />
    <p v-if="!isLoading" class="products-grid__counter">{{ productsToShowCount }} / {{ products.length }}</p>
  </section>
</template>

<style lang="scss" scoped>
.products-grid {
  margin-top: 0.5rem;

  :deep(.ods-button) {
    margin: 1rem auto;
    display: block;
  }

  &__counter {
    @include ods-typography-base;
    text-align: center;
  }
}
</style>
